import { AlarmClock, Download } from 'lucide-react';
import { useAudioStore } from '@/stores/audioStore';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import { formatRate, nextRate } from './helpers';

/**
 * Schlaf-Timer / Speed / Download button row. Used by Inline-Player
 * and Now-Playing Full-Screen.
 *
 * `onSchlafTimerClick` prop: when provided, the Schlaf-Timer button is
 * interactive and fires the handler on click. When undefined, the button
 * is rendered HTML-disabled as a defensive fallback (browser blocks click,
 * focus skipped, screen readers announce disabled state).
 *
 * Visual styling is identical regardless of interactive state (opacity-50,
 * matches Speed + Download per Figma spec).
 */
interface AudioMiniControlsProps {
  className?: string;
  onSchlafTimerClick?: () => void;
}

export function AudioMiniControls({ className, onSchlafTimerClick }: AudioMiniControlsProps) {
  const playbackRate = useAudioStore((s) => s.playbackRate);
  const setRate = useAudioStore((s) => s.setRate);
  const downloadUrl = useAudioStore((s) => s.downloadUrl);

  const copy = STRINGS.storyOutput.page.audio;
  const timerInert = !onSchlafTimerClick;

  return (
    <div className={cn('flex items-center justify-center gap-2 md:gap-5', className)}>
      <button
        type="button"
        onClick={onSchlafTimerClick}
        disabled={timerInert}
        aria-label={copy.ariaLabel.schlafTimer}
        className="flex h-10 flex-col items-center justify-center gap-1 text-dream-white opacity-50 disabled:cursor-default"
      >
        <AlarmClock className="h-[22px] w-[22px]" strokeWidth={2} />
        <span className="text-center text-xxs leading-none md:text-sm">{copy.schlafTimer}</span>
      </button>

      <button
        type="button"
        onClick={() => setRate(nextRate(playbackRate))}
        aria-label={copy.ariaLabel.speed}
        className="flex h-10 w-14 flex-col items-center justify-center gap-1 text-dream-white opacity-50 md:w-20"
      >
        <span className="flex h-[22px] items-center justify-center text-base font-medium leading-none">
          {formatRate(playbackRate)}×
        </span>
        <span className="text-center text-xxs leading-none md:text-sm">{copy.speedLabel}</span>
      </button>

      {downloadUrl ? (
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={copy.ariaLabel.download}
          className="flex h-10 w-14 flex-col items-center justify-center gap-1 text-dream-white opacity-50 md:w-20"
        >
          <Download className="h-[22px] w-[22px]" strokeWidth={2} />
          <span className="text-center text-xxs leading-none md:text-sm">{copy.download}</span>
        </a>
      ) : (
        <span aria-hidden className="h-10 w-14 md:w-20" />
      )}
    </div>
  );
}
